import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  PhotoIcon,
  VideoCameraIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { CreateBlogDto, UpdateBlogDto, ContentBlockType, BlogStatus, ContentBlock } from '../types/blog';
import RichTextEditor from './RichTextEditor';
import { processContentForExport } from '../utils/styleConverter';

interface SimpleBlogFormProps {
  initialData?: Partial<CreateBlogDto>;
  onSubmit: (data: CreateBlogDto | UpdateBlogDto) => Promise<void>;
  isLoading?: boolean;
  isEdit?: boolean;
}

const SimpleBlogForm: React.FC<SimpleBlogFormProps> = ({
  initialData,
  onSubmit,
  isLoading = false,
  isEdit = false,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [tags, setTags] = useState<string[]>(initialData?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [contentBlocks, setContentBlocks] = useState<ContentBlock[]>(
    initialData?.contentBlocks || []
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateBlogDto>({
    defaultValues: {
      title: initialData?.title || '',
      slug: initialData?.slug || '',
      excerpt: initialData?.excerpt || '',
      featuredImage: initialData?.featuredImage || '',
      status: initialData?.status || BlogStatus.DRAFT,
      priority: initialData?.priority || 0,
      metaTitle: initialData?.metaTitle || '',
      metaDescription: initialData?.metaDescription || '',
      allowComments: initialData?.allowComments ?? true,
      tags: initialData?.tags || [],
    },
  });

  const watchedData = watch();

  useEffect(() => {
    setValue('tags', tags);
    setValue('contentBlocks', contentBlocks as any);
  }, [tags, contentBlocks, setValue]);

  const addContentBlock = (type: ContentBlockType) => {
    const newBlock: ContentBlock = {
      type,
      content: '',
      order: contentBlocks.length,
    };

    if (type === ContentBlockType.IMAGE) {
      newBlock.alt = '';
      newBlock.caption = '';
    } else if (type === ContentBlockType.VIDEO) {
      newBlock.videoThumbnail = '';
      newBlock.caption = '';
    }

    setContentBlocks([...contentBlocks, newBlock]);
  };

  const updateContentBlock = (index: number, field: keyof ContentBlock, value: string) => {
    const updatedBlocks = [...contentBlocks];
    updatedBlocks[index] = { ...updatedBlocks[index], [field]: value };
    setContentBlocks(updatedBlocks);
  };

  const removeContentBlock = (index: number) => {
    const updatedBlocks = contentBlocks.filter((_, i) => i !== index);
    setContentBlocks(updatedBlocks);
  };

  const moveContentBlock = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < contentBlocks.length) {
      const updatedBlocks = [...contentBlocks];
      [updatedBlocks[index], updatedBlocks[newIndex]] = [updatedBlocks[newIndex], updatedBlocks[index]];
      // Update the order property for all blocks
      const reorderedBlocks = updatedBlocks.map((block, idx) => ({
        ...block,
        order: idx,
      }));
      setContentBlocks(reorderedBlocks);
    }
  };

  const handleTagAdd = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!tags.includes(tagInput.trim())) {
        setTags([...tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    if (!isEdit || !watchedData.slug) {
      setValue('slug', generateSlug(title));
    }
  };

  const renderContentBlockEditor = (block: ContentBlock, index: number) => {
    return (
      <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            {block.type === ContentBlockType.TEXT && <DocumentTextIcon className="h-5 w-5 text-gray-500" />}
            {block.type === ContentBlockType.IMAGE && <PhotoIcon className="h-5 w-5 text-gray-500" />}
            {block.type === ContentBlockType.VIDEO && <VideoCameraIcon className="h-5 w-5 text-gray-500" />}
            <span className="text-sm font-medium text-gray-700 capitalize">{block.type} Block</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => moveContentBlock(index, 'up')}
              disabled={index === 0}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <ArrowUpIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => moveContentBlock(index, 'down')}
              disabled={index === contentBlocks.length - 1}
              className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <ArrowDownIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => removeContentBlock(index)}
              className="p-1 text-red-400 hover:text-red-600"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {block.type === ContentBlockType.TEXT && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <RichTextEditor
                value={block.content}
                onChange={(value) => updateContentBlock(index, 'content', value)}
              />
            </div>
          )}

          {block.type === ContentBlockType.IMAGE && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image URL
                </label>
                <input
                  type="url"
                  value={block.content}
                  onChange={(e) => updateContentBlock(index, 'content', e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="https://example.com/image.jpg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Alt Text
                </label>
                <input
                  type="text"
                  value={block.alt || ''}
                  onChange={(e) => updateContentBlock(index, 'alt', e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Describe the image for accessibility"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Caption (Optional)
                </label>
                <input
                  type="text"
                  value={block.caption || ''}
                  onChange={(e) => updateContentBlock(index, 'caption', e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Image caption"
                />
              </div>
            </>
          )}

          {block.type === ContentBlockType.VIDEO && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video URL (YouTube, Vimeo, or direct link)
                </label>
                <input
                  type="url"
                  value={block.content}
                  onChange={(e) => updateContentBlock(index, 'content', e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="https://youtube.com/watch?v=..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Video Thumbnail URL (Optional)
                </label>
                <input
                  type="url"
                  value={block.videoThumbnail || ''}
                  onChange={(e) => updateContentBlock(index, 'videoThumbnail', e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="https://example.com/thumbnail.jpg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Caption (Optional)
                </label>
                <input
                  type="text"
                  value={block.caption || ''}
                  onChange={(e) => updateContentBlock(index, 'caption', e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Video caption"
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderPreview = () => {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{watchedData.title}</h1>
        {watchedData.excerpt && (
          <p className="text-lg text-gray-600 mb-6">{watchedData.excerpt}</p>
        )}
        {watchedData.featuredImage && (
          <img
            src={watchedData.featuredImage}
            alt={watchedData.title}
            className="w-full h-64 object-cover rounded-lg mb-6"
          />
        )}
        <div className="space-y-6">
          {contentBlocks.map((block, index) => (
            <div key={index}>
              {block.type === ContentBlockType.TEXT && (
                <div>
                  {process.env.NODE_ENV === 'development' && (
                    <div className="mb-2 text-xs bg-yellow-100 p-2 rounded">
                      <strong>Debug:</strong> {block.content ? `Content length: ${block.content.length}` : 'No content'}
                    </div>
                  )}
                  <div
                    className="blog-content"
                    dangerouslySetInnerHTML={{ __html: block.content || '' }}
                  />
                </div>
              )}
              {block.type === ContentBlockType.IMAGE && block.content && (
                <div>
                  <img
                    src={block.content}
                    alt={block.alt || ''}
                    className="w-full rounded-lg"
                  />
                  {block.caption && (
                    <p className="text-sm text-gray-600 mt-2 text-center italic">
                      {block.caption}
                    </p>
                  )}
                </div>
              )}
              {block.type === ContentBlockType.VIDEO && block.content && (
                <div>
                  <div className="aspect-w-16 aspect-h-9">
                    {block.content.includes('youtube.com') || block.content.includes('youtu.be') ? (
                      <iframe
                        src={block.content.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                        frameBorder="0"
                        allowFullScreen
                        title="YouTube video player"
                        className="w-full h-64 rounded-lg"
                      />
                    ) : block.content.includes('vimeo.com') ? (
                      <iframe
                        src={block.content.replace('vimeo.com/', 'player.vimeo.com/video/')}
                        frameBorder="0"
                        allowFullScreen
                        title="Vimeo video player"
                        className="w-full h-64 rounded-lg"
                      />
                    ) : (
                      <video
                        src={block.content}
                        controls
                        className="w-full h-64 rounded-lg"
                        poster={block.videoThumbnail}
                      />
                    )}
                  </div>
                  {block.caption && (
                    <p className="text-sm text-gray-600 mt-2 text-center italic">
                      {block.caption}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        {tags.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleFormSubmit = async (data: CreateBlogDto) => {
    // Clean content blocks by removing MongoDB-specific fields and applying inline CSS
    const cleanedContentBlocks = contentBlocks.map(block => {
      // Create a shallow copy to avoid mutating state
      const newBlock = { ...block };

      // Apply inline CSS for Text blocks
      if (newBlock.type === ContentBlockType.TEXT && newBlock.content) {
        newBlock.content = processContentForExport(newBlock.content);
      }

      const { _id, createdAt, updatedAt, ...cleanBlock } = newBlock as any;
      return cleanBlock;
    });

    const formData = {
      ...data,
      tags,
      contentBlocks: cleanedContentBlocks,
    };
    await onSubmit(formData);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Blog Post' : 'Create New Blog Post'}
        </h2>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => setShowPreview(!showPreview)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <EyeIcon className="-ml-1 mr-2 h-4 w-4" />
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
      </div>

      {showPreview ? (
        renderPreview()
      ) : (
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                  Title *
                </label>
                <input
                  type="text"
                  {...register('title', { required: 'Title is required' })}
                  onChange={handleTitleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Enter blog title"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                  Slug
                </label>
                <input
                  type="text"
                  {...register('slug')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="url-friendly-slug"
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700">
                  Excerpt
                </label>
                <textarea
                  {...register('excerpt')}
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Brief description of the blog post"
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="featuredImage" className="block text-sm font-medium text-gray-700">
                  Featured Image URL
                </label>
                <input
                  type="url"
                  {...register('featuredImage')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  {...register('status')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value={BlogStatus.DRAFT}>Draft</option>
                  <option value={BlogStatus.PUBLISHED}>Published</option>
                  <option value={BlogStatus.UNPUBLISHED}>Unpublished</option>
                </select>
              </div>

              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
                  Priority
                </label>
                <input
                  type="number"
                  {...register('priority', { valueAsNumber: true })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tags</h3>
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-2">
                Add Tags
              </label>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagAdd}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Type a tag and press Enter"
              />
              {tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-500 focus:outline-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Content Blocks */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Content Blocks</h3>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => addContentBlock(ContentBlockType.TEXT)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <DocumentTextIcon className="-ml-1 mr-1 h-4 w-4" />
                  Text
                </button>
                <button
                  type="button"
                  onClick={() => addContentBlock(ContentBlockType.IMAGE)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <PhotoIcon className="-ml-1 mr-1 h-4 w-4" />
                  Image
                </button>
                <button
                  type="button"
                  onClick={() => addContentBlock(ContentBlockType.VIDEO)}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  <VideoCameraIcon className="-ml-1 mr-1 h-4 w-4" />
                  Video
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {contentBlocks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2">No content blocks yet. Add your first block above.</p>
                </div>
              ) : (
                contentBlocks.map((block, index) => renderContentBlockEditor(block, index))
              )}
            </div>
          </div>

          {/* SEO Settings */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">SEO Settings</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="metaTitle" className="block text-sm font-medium text-gray-700">
                  Meta Title
                </label>
                <input
                  type="text"
                  {...register('metaTitle')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="SEO title (max 60 characters)"
                  maxLength={60}
                />
              </div>
              <div>
                <label htmlFor="metaDescription" className="block text-sm font-medium text-gray-700">
                  Meta Description
                </label>
                <textarea
                  {...register('metaDescription')}
                  rows={3}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="SEO description (max 160 characters)"
                  maxLength={160}
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('allowComments')}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="allowComments" className="ml-2 block text-sm text-gray-900">
                  Allow comments on this post
                </label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving...' : isEdit ? 'Update Blog' : 'Create Blog'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SimpleBlogForm; 